import React, { createContext, useContext, useState, useEffect } from 'react';
import API from '../api/axios';
import { getCurrentLocation, getDistanceKm, isWithinServiceArea } from '../utils/location';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const isExcludedLocationRoute = () => {
  if (typeof window === 'undefined' || !window.location) return false;
  const path = (window.location.pathname || '').toLowerCase();
  return (
    path.includes('terms') ||
    path.includes('privacy') ||
    path.includes('policy') ||
    path.includes('legal')
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('sc_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const syncCartToBackend = async (newCart) => {
    try {
      localStorage.setItem('sc_cart', JSON.stringify(newCart));
      if (localStorage.getItem('sc_token')) {
        await API.put('/cart', { cart: newCart });
      }
    } catch (err) {
      console.warn('Failed to sync cart to backend:', err.message || err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('sc_token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const autoSelectClosestAddress = async (userData) => {
    try {
      // Strictly avoid any location checks on terms, privacy-policy, or legal pages
      if (isExcludedLocationRoute()) {
        return;
      }

      // Only proceed if the user has ALREADY granted location permissions previously.
      // Do not trigger unexpected background browser permission popups on load.
      if (typeof navigator !== 'undefined' && navigator.permissions && navigator.permissions.query) {
        try {
          const perm = await navigator.permissions.query({ name: 'geolocation' });
          if (perm.state !== 'granted') {
            return;
          }
        } catch {
          return;
        }
      } else {
        return;
      }

      const currentLoc = await getCurrentLocation();
      
      if (!isWithinServiceArea(currentLoc.lat, currentLoc.lng)) {
        console.log('User is outside Jhajjar city delivery area');
        return;
      }
      
      const saved = userData.savedAddresses || [];
      if (saved.length === 0) return;
      
      let closestAddr = null;
      let minDistance = Infinity;
      for (const addr of saved) {
        const d = getDistanceKm(currentLoc.lat, currentLoc.lng, addr.lat, addr.lng);
        if (d < minDistance) {
          minDistance = d;
          closestAddr = addr;
        }
      }
      
      if (closestAddr) {
        const currentActive = userData.address;
        const isAlreadyActive = currentActive && Math.abs(currentActive.lat - closestAddr.lat) < 0.0001 && Math.abs(currentActive.lng - closestAddr.lng) < 0.0001;
        
        if (!isAlreadyActive) {
          const { data } = await API.put(`/users/address/select/${closestAddr._id}`);
          setUser(prev => prev ? { ...prev, address: data.address } : prev);
          console.log('Auto-selected closest address:', closestAddr.line1);
        }
      }
    } catch (err) {
      console.warn('Auto-select address failed:', err.message || err);
    }
  };

  const fetchUser = async (token) => {
    try {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await API.get('/auth/me');
      setUser(data.user);
      if (Array.isArray(data.user?.cart)) {
        setCart(prev => {
          const local = prev || [];
          if (local.length === 0) {
            try { localStorage.setItem('sc_cart', JSON.stringify(data.user.cart)); } catch (e) {}
            return data.user.cart;
          }
          // Merge local items with backend cart (backend takes priority, local items appended if new)
          const merged = [...data.user.cart];
          let changed = false;
          for (const item of local) {
            const exists = merged.some(i => i.id === item.id && i.type === item.type);
            if (!exists) {
              merged.push(item);
              changed = true;
            }
          }
          try { localStorage.setItem('sc_cart', JSON.stringify(merged)); } catch (e) {}
          if (changed) {
            API.put('/cart', { cart: merged }).catch(() => {});
          }
          return merged;
        });
      }
      if (data.user && data.user.savedAddresses && data.user.savedAddresses.length > 0 && !isExcludedLocationRoute()) {
        autoSelectClosestAddress(data.user);
      }
    } catch (error) {
      // Only clear token on 401 (invalid/expired token) — not on 429, network errors, etc.
      if (error.response?.status === 401) {
        localStorage.removeItem('sc_token');
        delete API.defaults.headers.common['Authorization'];
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('sc_token', token);
    API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    if (Array.isArray(userData?.cart)) {
      setCart(prev => {
        const local = prev || [];
        if (local.length === 0) {
          try { localStorage.setItem('sc_cart', JSON.stringify(userData.cart)); } catch (e) {}
          return userData.cart;
        }
        const merged = [...userData.cart];
        let changed = false;
        for (const item of local) {
          const exists = merged.some(i => i.id === item.id && i.type === item.type);
          if (!exists) {
            merged.push(item);
            changed = true;
          }
        }
        try { localStorage.setItem('sc_cart', JSON.stringify(merged)); } catch (e) {}
        if (changed) {
          API.put('/cart', { cart: merged }).catch(() => {});
        }
        return merged;
      });
    }
    if (userData && userData.savedAddresses && userData.savedAddresses.length > 0 && !isExcludedLocationRoute()) {
      autoSelectClosestAddress(userData);
    }
  };

  const logout = () => {
    localStorage.removeItem('sc_token');
    localStorage.removeItem('sc_cart');
    delete API.defaults.headers.common['Authorization'];
    setUser(null);
    setCart([]);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  // Cart functions
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id && i.type === item.type);
      let next;
      if (existing) {
        if (item.type === 'print') {
          next = prev.map(i => 
            i.id === item.id && i.type === item.type 
              ? { ...i, ...item, quantity: 1 } 
              : i
          );
        } else {
          next = prev.map(i => 
            i.id === item.id && i.type === item.type 
              ? { ...i, quantity: i.quantity + (item.quantity ?? 1) } 
              : i
          );
        }
      } else {
        next = [...prev, { ...item, quantity: item.quantity ?? 1 }];
      }
      syncCartToBackend(next);
      return next;
    });
  };

  const updateCartItem = (id, type, updatedItem) => {
    setCart(prev => {
      const next = prev.map(i =>
        i.id === id && i.type === type ? { ...updatedItem, quantity: updatedItem.quantity || 1 } : i
      );
      syncCartToBackend(next);
      return next;
    });
  };

  const removeFromCart = (id, type) => {
    setCart(prev => {
      const next = prev.filter(i => !(i.id === id && i.type === type));
      syncCartToBackend(next);
      return next;
    });
  };

  const updateCartQuantity = (id, type, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id, type);
      return;
    }
    setCart(prev => {
      const next = prev.map(i => 
        i.id === id && i.type === type ? { ...i, quantity } : i
      );
      syncCartToBackend(next);
      return next;
    });
  };

  const clearCart = () => {
    setCart([]);
    syncCartToBackend([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, updateUser,
      cart, addToCart, updateCartItem, removeFromCart, updateCartQuantity, clearCart, cartTotal, cartCount
    }}>
      {children}
    </AuthContext.Provider>
  );
};
